import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import './ConfirmDialog.css';

// Ask-before-destroying, as one shared primitive.
//
// Nothing in this app asked before deleting: one stray click permanently
// removed a production batch, a formula version, an invoice. Some of that is
// genuinely irreplaceable — a batch is a record of something that physically
// happened and cannot be re-derived from anything else.
//
// Built as a promise-returning hook rather than a component each page renders,
// because the alternative is fifteen pages each carrying `pendingDelete`
// state and a dialog in their JSX. Here a call site stays one line:
//
//   if (!(await confirm({ title: 'Delete this batch?' }))) return;
//
// Deliberately not window.confirm: it can't be styled, it says "localhost
// says", and it can't tell you *what* you're about to lose.

const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  // The promise's resolve, held across renders so the buttons can settle it.
  const resolver = useRef(null);
  const confirmRef = useRef(null);

  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        resolver.current = resolve;
        setRequest({
          title: 'Are you sure?',
          body: null,
          confirmLabel: 'Delete',
          cancelLabel: 'Cancel',
          tone: 'danger',
          ...options,
        });
      }),
    []
  );

  const settle = useCallback((answer) => {
    setRequest(null);
    // Guarded: a second click (or Escape landing with the click) must not
    // resolve an already-settled promise.
    const resolve = resolver.current;
    resolver.current = null;
    if (resolve) resolve(answer);
  }, []);

  useEffect(() => {
    if (!request) return;
    // Focus the confirm button so the dialog is keyboard-usable immediately,
    // and Escape always cancels.
    confirmRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') settle(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, settle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request && (
        <div
          className="confirm-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          // Clicking the backdrop cancels; clicking inside must not.
          onClick={() => settle(false)}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 id="confirm-title">{request.title}</h3>
            {request.body && <p className="confirm-body">{request.body}</p>}
            <div className="confirm-actions">
              <button type="button" className="ghost-button" onClick={() => settle(false)}>
                {request.cancelLabel}
              </button>
              <button
                ref={confirmRef}
                type="button"
                className={'confirm-go ' + request.tone}
                onClick={() => settle(true)}
              >
                {request.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
